import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getContactUsList } from "../../api/contact";
import {
  MdOutlineArrowLeft,
  MdOutlineArrowRight,
  MdClose,
  MdOutlineMarkEmailUnread,
  MdArrowUpward,
  MdArrowDownward,
  MdUnfoldMore,
} from "react-icons/md";
import { CiFilter } from "react-icons/ci";
import DynamicSVG from "../../components/dashboard/venue-table/dynmaic-svg";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

export default function ContactUsRequests() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [accountType, setAccountType] = useState("");
  const [status, setStatus] = useState("");
  const [deviceCategory, setDeviceCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedRow, setSelectedRow] = useState(null);

  // Popover
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [filterField, setFilterField] = useState(null);
  const popoverRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: [
      "contactUsList",
      currentPage,
      itemsPerPage,
      search,
      accountType,
      status,
      deviceCategory,
      dateFrom,
      dateTo,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getContactUsList({
        page: currentPage,
        limit: itemsPerPage,
        ...(search && { search }),
        ...(accountType && { accountType }),
        ...(status && { status }),
        ...(deviceCategory && { deviceCategory }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        sortBy,
        sortOrder,
      }),
    refetchOnWindowFocus: false,
  });

  const totalPages = data?.pagination?.totalPages || 1;
  const total = data?.pagination?.total || 0;

  // Close popover on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    if (showPopover) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPopover]);

  const handleFilterPopover = (e, field) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const POPOVER_WIDTH = 256; // w-64
    const wouldOverflow = rect.left + POPOVER_WIDTH > window.innerWidth - 8;
    setPopoverPosition({
      top: rect.bottom + window.scrollY + 6,
      left: wouldOverflow
        ? rect.right + window.scrollX - POPOVER_WIDTH
        : rect.left + window.scrollX,
    });
    setFilterField(field);
    setShowPopover(true);
  };

  const clearFieldFilter = (field) => {
    if (field === "email") setSearch("");
    else if (field === "accountType") setAccountType("");
    else if (field === "status") setStatus("");
    else if (field === "deviceCategory") setDeviceCategory("");
    else if (field === "createdAt") { setDateFrom(""); setDateTo(""); }
    setCurrentPage(1);
  };

  const isFilterActive = (field) => {
    if (field === "email") return !!search;
    if (field === "accountType") return !!accountType;
    if (field === "status") return !!status;
    if (field === "deviceCategory") return !!deviceCategory;
    if (field === "createdAt") return !!(dateFrom || dateTo);
    return false;
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("desc"); }
    setCurrentPage(1);
  };

  // ── Header builders ──────────────────────────────────────────────────────
  const FilterIcon = ({ field }) => (
    <CiFilter
      className={`w-4 h-4 shrink-0 cursor-pointer transition-colors ${
        isFilterActive(field) ? "text-blue-600" : "text-gray-400 hover:text-blue-500"
      }`}
      onClick={(e) => handleFilterPopover(e, field)}
    />
  );

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <MdUnfoldMore className="inline text-gray-300" />;
    return sortOrder === "asc"
      ? <MdArrowUpward className="inline text-blue-600" />
      : <MdArrowDownward className="inline text-blue-600" />;
  };

  // Sortable + filterable header
  const colHeader = (label, sortField, filterFld) => (
    <div className="flex items-center gap-1.5">
      <div
        className="flex items-center gap-0.5 cursor-pointer select-none group"
        onClick={() => handleSort(sortField)}
      >
        <span className="group-hover:text-blue-600 transition-colors">{label}</span>
        <SortIcon field={sortField} />
      </div>
      <FilterIcon field={filterFld} />
    </div>
  );

  // Filter-only header (no sort)
  const filterHeader = (label, filterFld) => (
    <div className="flex items-center gap-1.5">
      <span>{label}</span>
      <FilterIcon field={filterFld} />
    </div>
  );

  // ── Popover content ───────────────────────────────────────────────────────
  const renderPopoverContent = () => {
    if (filterField === "email") {
      return (
        <input
          autoFocus
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
        />
      );
    }
    if (filterField === "accountType") {
      return (
        <select
          autoFocus
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={accountType}
          onChange={(e) => { setAccountType(e.target.value); setCurrentPage(1); }}
        >
          <option value="">All Types</option>
          <option value="individual">Individual</option>
          <option value="corporate">Corporate</option>
          <option value="eventPlanner">Event Planner</option>
        </select>
      );
    }
    if (filterField === "status") {
      return (
        <select
          autoFocus
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in-progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      );
    }
    if (filterField === "deviceCategory") {
      return (
        <select
          autoFocus
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={deviceCategory}
          onChange={(e) => { setDeviceCategory(e.target.value); setCurrentPage(1); }}
        >
          <option value="">All Devices</option>
          <option value="mobile">Mobile</option>
          <option value="desktop">Desktop</option>
          <option value="tablet">Tablet</option>
        </select>
      );
    }
    if (filterField === "createdAt") {
      return (
        <div className="space-y-2.5">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      );
    }
    return null;
  };

  // ── Badges ────────────────────────────────────────────────────────────────
  const getStatusBadge = (value) => {
    const map = {
      pending: "bg-amber-50 text-amber-700 border border-amber-200",
      "in-progress": "bg-blue-50 text-blue-700 border border-blue-200",
      resolved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      closed: "bg-gray-100 text-gray-500 border border-gray-200",
    };
    const cls = map[value] || "bg-gray-100 text-gray-500 border border-gray-200";
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
        {value || "pending"}
      </span>
    );
  };

  const getAccountTypeBadge = (value) => {
    const map = {
      corporate: "bg-cyan-100 text-cyan-700",
      eventPlanner: "bg-amber-100 text-amber-700",
      individual: "bg-violet-100 text-violet-700",
    };
    const labels = { corporate: "Corporate", eventPlanner: "Event Planner", individual: "Individual" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[value] || "bg-gray-100 text-gray-500"}`}>
        {labels[value] || value || "—"}
      </span>
    );
  };

  const getDeviceBadge = (value) => {
    const map = {
      mobile: "bg-sky-100 text-sky-700",
      desktop: "bg-indigo-100 text-indigo-700",
      tablet: "bg-purple-100 text-purple-700",
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[value] || "bg-gray-100 text-gray-500"}`}>
        {value || "—"}
      </span>
    );
  };

  // ── Pagination helpers ────────────────────────────────────────────────────
  const paginationPages = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 4, 5];
    if (currentPage >= totalPages - 2)
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-5 min-h-screen bg-gray-50">
      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Contact Us Requests</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {isLoading ? "Loading…" : `${total} total ${total === 1 ? "request" : "requests"}`}
          </p>
        </div>

        {/* Search only — filters are in column headers */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56 shadow-sm"
          />
        </div>
      </div>

      {/* ── Table Card ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <DataTable
          value={data?.data}
          loading={isLoading}
          stripedRows
          responsiveLayout="scroll"
          onRowClick={(e) => setSelectedRow(e.data)}
          rowClassName={() => "cursor-pointer transition-colors duration-150 hover:!bg-blue-50"}
          emptyMessage={
            <div className="flex flex-col items-center py-14 text-gray-300">
              <MdOutlineMarkEmailUnread className="text-6xl mb-3" />
              <p className="text-base font-medium text-gray-400">No contact requests found</p>
              <p className="text-sm text-gray-300 mt-1">Try adjusting your search or filters</p>
            </div>
          }
        >
          <Column
            header="#"
            style={{ width: "56px" }}
            body={(_, options) => (
              <DynamicSVG index={options.rowIndex} currentPage={currentPage} itemsPerPage={itemsPerPage} />
            )}
          />

          <Column
            field="email"
            header={colHeader("Email", "email", "email")}
            body={(row) => (
              <span className="text-sm font-medium text-gray-800">{row.email || "—"}</span>
            )}
          />

          <Column
            field="accountType"
            header={colHeader("Account Type", "accountType", "accountType")}
            body={(row) => getAccountTypeBadge(row.accountType)}
          />

          <Column
            field="subject"
            header="Subject"
            body={(row) => (
              <span className="text-sm text-gray-700 block max-w-[180px] truncate" title={row.subject}>
                {row.subject || "—"}
              </span>
            )}
          />

          <Column
            field="description"
            header="Description"
            body={(row) => (
              <span className="text-sm text-gray-400 block max-w-[160px] truncate" title={row.description}>
                {row.description || "—"}
              </span>
            )}
          />

          <Column
            field="deviceCategory"
            header={filterHeader("Device", "deviceCategory")}
            body={(row) => getDeviceBadge(row.deviceCategory)}
          />

          <Column
            field="status"
            header={colHeader("Status", "status", "status")}
            body={(row) => getStatusBadge(row.status)}
          />

          <Column
            field="ipAddress"
            header="IP Address"
            body={(row) => (
              <span className="font-mono text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">
                {row.ipAddress || "—"}
              </span>
            )}
          />

          <Column
            field="createdAt"
            header={colHeader("Date", "createdAt", "createdAt")}
            body={(row) => (
              <span className="text-sm text-gray-500">
                {moment(row.createdAt).format("DD MMM YYYY")}
              </span>
            )}
          />
        </DataTable>
      </div>

      {/* ── Pagination ── */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-5 gap-4">
        <p className="text-sm text-gray-400">
          Showing{" "}
          <span className="font-semibold text-gray-600">
            {total === 0 ? 0 : Math.min((currentPage - 1) * itemsPerPage + 1, total)}
            –{Math.min(currentPage * itemsPerPage, total)}
          </span>{" "}
          of <span className="font-semibold text-gray-600">{total}</span> results
        </p>

        <div className="flex items-center gap-3">
          <select
            value={itemsPerPage}
            onChange={(e) => { setCurrentPage(1); setItemsPerPage(parseInt(e.target.value)); }}
            className="py-1.5 px-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <MdOutlineArrowLeft className="text-xl text-gray-600" />
            </button>

            {paginationPages().map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition ${
                  currentPage === page
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <MdOutlineArrowRight className="text-xl text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter Popover ── */}
      {showPopover && (
        <div
          ref={popoverRef}
          className="fixed z-[999] bg-white border border-gray-200 rounded-xl shadow-2xl w-64 py-4 px-4 space-y-3"
          style={{ top: popoverPosition.top, left: popoverPosition.left }}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Filter by{" "}
              {filterField === "createdAt"
                ? "Date"
                : filterField === "deviceCategory"
                ? "Device"
                : filterField === "accountType"
                ? "Account Type"
                : filterField}
            </p>
            <button
              onClick={() => setShowPopover(false)}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <MdClose className="text-base" />
            </button>
          </div>

          {renderPopoverContent()}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { clearFieldFilter(filterField); setShowPopover(false); }}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
            >
              Clear
            </button>
            <button
              onClick={() => setShowPopover(false)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedRow && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedRow(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Request Details</h3>
                <p className="text-sm text-gray-400 mt-0.5">
                  {moment(selectedRow.createdAt).format("DD MMM YYYY, h:mm A")}
                </p>
              </div>
              <button
                onClick={() => setSelectedRow(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition mt-0.5"
              >
                <MdClose className="text-gray-500 text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email</p>
                  <p className="text-sm font-medium text-gray-800 break-all">{selectedRow.email}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Account Type</p>
                  {getAccountTypeBadge(selectedRow.accountType)}
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Status</p>
                  {getStatusBadge(selectedRow.status)}
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Device</p>
                  {getDeviceBadge(selectedRow.deviceCategory)}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Subject</p>
                <p className="text-sm font-medium text-gray-800 bg-gray-50 rounded-lg px-3 py-2.5">
                  {selectedRow.subject || "—"}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Description</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-3 leading-relaxed whitespace-pre-wrap min-h-[60px]">
                  {selectedRow.description || "—"}
                </p>
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Technical Info</p>
                <div className="flex items-start gap-3">
                  <span className="text-xs text-gray-400 w-20 shrink-0 pt-0.5">IP Address</span>
                  <span className="font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                    {selectedRow.ipAddress || "—"}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-xs text-gray-400 w-20 shrink-0 pt-0.5">User Agent</span>
                  <span className="text-xs text-gray-500 break-all leading-relaxed">
                    {selectedRow.userAgent || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
